/**
 * <dd-reviews> — Custom reviews web component
 *
 * Fetches product reviews from the Vercel proxy that calls the Klaviyo
 * Reviews API, and posts new reviews to Klaviyo's public client endpoint
 * (POST /client/reviews) using the 6-char company_id (no private key in
 * the browser).
 *
 * Read path:  GET  {apiUrl}/reviews?product_id=<id>
 * Write path: POST https://a.klaviyo.com/client/reviews?company_id=<co>
 *
 * Attributes:
 *   data-product-id          — Shopify numeric product ID (required)
 *   data-api-url             — Reviews proxy base URL (required for read)
 *   data-proxy-token         — X-DD-Reviews-Token for the read proxy
 *   data-klaviyo-company-id  — Klaviyo public key; omit to hide write form
 *   data-product-name        — used in the write-form product chip
 *   data-product-url         — canonical URL posted back to Klaviyo
 *   data-product-image       — optional featured image for Klaviyo dashboard
 *   data-customer-name       — pre-fills author if logged in
 *   data-customer-email      — verified email used at submit (logged-in only)
 *   data-customer-type       — raw Helium Customer Fields "professional_type"
 *                              value (e.g. "Stylist", "Salon Owner", "Student").
 *                              Blank for legacy accounts; normalizer falls
 *                              back to "Verified Stylist". Sent as
 *                              `metadata.customer_type` so review display
 *                              can render the right badge + icon later.
 *   data-customer-logged-in  — "true" | "false" — gates the submit form
 *   data-login-url           — `/account/login?return_url=<pdp>` for guests
 */
if (!customElements.get('dd-reviews')) {
  // Klaviyo endpoint/revision constants live in `dd-klaviyo-review-core.js`
  // (window.DDKlaviyoReview) so both this section and the sitewide review
  // nudge post against the same contract. Load order in theme.liquid:
  // dd-klaviyo-review-core.js → dd-reviews.js.

  class DDReviews extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.apiUrl = this.dataset.apiUrl;
      this.proxyToken = this.dataset.proxyToken || '';
      if (!this.productId || !this.apiUrl) return;

      this.klaviyoCompanyId = this.dataset.klaviyoCompanyId || '';
      this.productName = this.dataset.productName || '';
      this.productUrl = this.dataset.productUrl || '';
      this.productImage = this.dataset.productImage || '';
      this.customerName = this.dataset.customerName || '';
      this.customerEmail = this.dataset.customerEmail || '';
      this.customerType = this.dataset.customerType || '';
      this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
      this.loginUrl = this.dataset.loginUrl || '/account/login';

      this._allReviews = [];
      this._nextCursor = null;
      this._sortBy = '-created';
      this._loading = false;

      /* Client-side pagination cap on top of whatever Klaviyo returns.
         Klaviyo's Reviews API tends to return up to ~100 reviews in a
         single response with no `next_cursor`, so gating the load-more
         button purely on `_nextCursor` meant the button never showed
         for brands with 10-99 reviews. We instead always cap visible
         count at `_pageSize` and let the button advance the window;
         the click handler still calls `_fetchReviews(cursor)` when the
         in-memory list runs out AND Klaviyo has more pages.
         NOT reset on sort change — if a user has expanded to 27 cards,
         re-sorting should reorder those 27, not collapse back to 9. */
      this._pageSize = 9;
      this._visibleCount = this._pageSize;

      this._skeleton = this.querySelector('.dd-reviews__skeleton');
      this._body = this.querySelector('.dd-reviews__body');

      this._fetchReviews();

      // Deep-link from external surfaces (e.g. the sitewide
      // <dd-review-nudge> "Write a review" CTA) -> auto-open the write modal.
      // Independent of the reviews fetch — the modal is a write surface only,
      // so opening it before the read pipeline returns is intentional. The URL
      // param is consumed (replaceState) so back/forward don't re-open it.
      this._maybeAutoOpenWriteModal();

      /* NOTE: We deliberately DON'T auto-open the modal on plain
         PDP visits when a draft exists. Auto-popping a modal on
         every product page within the 30-min TTL is intrusive — a
         user who walked away mid-review and came back to look at
         the price doesn't want a modal stealing focus. Instead the
         draft is silently preserved and rehydrated when the user
         clicks "Write a review" again (see `_openWriteModal` —
         missing `prefill` arg falls through to `_readDraft`). The
         eviction recovery path is now: reload → user clicks "Write
         a review" → modal opens with everything pre-filled. One
         extra tap, but no surprise modals. */
    }

    _maybeAutoOpenWriteModal() {
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('write_review') !== '1') return false;
        url.searchParams.delete('write_review');
        var clean = url.pathname + (url.search ? url.search : '') + (url.hash || '');
        window.history.replaceState(null, '', clean);
        // Defer one frame so layout/scroll restoration settles before the
        // modal mounts and steals focus. _openWriteModal will pick up any
        // existing draft via the no-prefill auto-hydrate path.
        requestAnimationFrame(() => this._openWriteModal());
        return true;
      } catch (_) {
        /* non-fatal — auto-open is a convenience, not a requirement */
        return false;
      }
    }

    /* ── Draft persistence (Tier-2 reload survival) ──────────────
       The write modal lives entirely in JS state. iOS Safari
       evicts background tabs aggressively when the system photo
       picker is open, especially with multi-MB HEIC `File` objects
       held in memory. When that happens Safari does a full reload
       on resume — without a draft, the user loses their typed
       review *and* the photos they'd already picked. We checkpoint
       the form state to sessionStorage on every input change so a
       reload only loses the *in-flight* picker session, not the
       review draft itself.

       Two keys per product, intentionally split:

         dd-rv-draft:<productId>         — text fields (small, ~1KB)
         dd-rv-draft-photos:<productId>  — encoded photo data URIs
                                            (large, can be >500KB)

       The split exists for performance: every keystroke writes the
       text key (cheap), but the photo key is only written when the
       photo set changes (rare). Without the split, every keystroke
       would re-stringify the entire photo payload — which is
       measurably slow on Safari with 4 photos attached and surfaces
       as visible textarea input lag.

       Keyed by productId so a user reviewing two PDPs in two tabs
       doesn't cross-contaminate. Versioned (`v: 1`) so a future
       schema change can ignore old payloads safely. Timestamped on
       the text key so drafts older than DRAFT_TTL_MS get treated as
       stale and ignored at restore time. */

    _draftTextKey() { return `dd-rv-draft:${this.productId}`; }
    _draftPhotosKey() { return `dd-rv-draft-photos:${this.productId}`; }

    _readDraft() {
      try {
        const rawText = sessionStorage.getItem(this._draftTextKey());
        if (!rawText) return null;
        const text = JSON.parse(rawText);
        if (!text || typeof text !== 'object') return null;
        if (text.v !== 1) return null;
        if (text.productId !== this.productId) return null;
        const DRAFT_TTL_MS = 30 * 60 * 1000;
        if (!text.ts || Date.now() - text.ts > DRAFT_TTL_MS) {
          this._clearDraft();
          return null;
        }
        // Photos read from the sibling key — independently versioned
        // so a wiped-but-not-text-wiped photo blob (e.g. quota
        // pressure on the photo write) doesn't poison the whole
        // draft. Photo array is deliberately validated as
        // strings-only; any non-string entry drops the whole photo
        // set rather than risking a corrupt restore.
        let photos = [];
        try {
          const rawPhotos = sessionStorage.getItem(this._draftPhotosKey());
          if (rawPhotos) {
            const parsed = JSON.parse(rawPhotos);
            if (parsed && parsed.v === 1 && parsed.productId === this.productId &&
                Array.isArray(parsed.photos) &&
                parsed.photos.every((p) => typeof p === 'string')) {
              photos = parsed.photos;
            }
          }
        } catch (_) { photos = []; }

        return {
          v: 1,
          ts: text.ts,
          productId: text.productId,
          rating: typeof text.rating === 'string' ? text.rating : '',
          title: typeof text.title === 'string' ? text.title : '',
          content: typeof text.content === 'string' ? text.content : '',
          author: typeof text.author === 'string' ? text.author : '',
          photos,
        };
      } catch (_) {
        return null;
      }
    }

    /* Hot path — runs on every keystroke. Intentionally writes ONLY
       the text key (typically <1KB) so JSON.stringify + setItem
       cost stays in the sub-millisecond range even on older devices.
       The photo blob is persisted separately by `_saveDraftPhotos`
       on add/remove. */
    _saveDraftText() {
      const modal = this._writeModal;
      if (!modal || !this.productId) return;
      try {
        const ratingEl = modal.querySelector('[data-dd-rv-rating-value]');
        const titleEl = modal.querySelector('#dd-rv-title');
        const contentEl = modal.querySelector('#dd-rv-content');
        const authorEl = modal.querySelector('#dd-rv-author');
        const text = {
          v: 1,
          ts: Date.now(),
          productId: this.productId,
          rating: ratingEl ? ratingEl.value || '' : '',
          title: titleEl ? titleEl.value || '' : '',
          content: contentEl ? contentEl.value || '' : '',
          author: authorEl ? authorEl.value || '' : '',
        };
        sessionStorage.setItem(this._draftTextKey(), JSON.stringify(text));
      } catch (_) {
        /* sessionStorage can throw under quota pressure or in
           Safari Private Browsing (which exposes a ~0 quota).
           Failing silently is correct: the modal still works, the
           user just doesn't get reload-survival. */
      }
    }

    /* Cold path — only runs on photo add / remove / hydrate. The
       photo payload can be 500KB+ so we don't tie this to keystrokes.
       Empty array is still written (rather than removed) so a user
       who removes all photos doesn't accidentally restore an older
       photo set on next reload. */
    _saveDraftPhotos() {
      if (!this.productId) return;
      try {
        const photos = Array.isArray(this._pendingPhotos)
          ? this._pendingPhotos.map((p) => p && p.dataUri).filter(Boolean)
          : [];
        const payload = {
          v: 1,
          ts: Date.now(),
          productId: this.productId,
          photos,
        };
        sessionStorage.setItem(this._draftPhotosKey(), JSON.stringify(payload));
      } catch (_) {
        /* Photo persistence is best-effort. If quota fills, photo
           recovery is lost but text recovery still works via
           _saveDraftText. */
      }
    }

    _clearDraft() {
      try {
        sessionStorage.removeItem(this._draftTextKey());
        sessionStorage.removeItem(this._draftPhotosKey());
      } catch (_) { /* noop */ }
    }

    async _fetchReviews(cursor) {
      if (this._loading) return;
      this._loading = true;

      const url = new URL(`${this.apiUrl}/reviews`);
      url.searchParams.set('product_id', this.productId);
      if (cursor) url.searchParams.set('page_cursor', cursor);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      try {
        const fetcher = window.DDKlaviyoReview?.fetchReviewsProxy || fetch;
        const res = await fetcher(url.toString(), this.proxyToken, { signal: controller.signal });
        clearTimeout(timeout);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.error) throw new Error(data.error);

        this._allReviews = this._allReviews.concat(data.reviews || []);
        this._nextCursor = data.next_cursor || null;
        this._render();
      } catch (err) {
        clearTimeout(timeout);
        if (this._allReviews.length === 0) {
          this._renderError();
        }
      } finally {
        this._loading = false;
      }
    }

    _getSorted() {
      const reviews = [...this._allReviews];
      switch (this._sortBy) {
        case '-rating':
          reviews.sort((a, b) => b.rating - a.rating);
          break;
        case 'rating':
          reviews.sort((a, b) => a.rating - b.rating);
          break;
        case 'images':
          reviews.sort((a, b) => (b.images?.length || 0) - (a.images?.length || 0));
          break;
        default:
          reviews.sort((a, b) => new Date(b.created) - new Date(a.created));
      }
      return reviews;
    }

    // Reviews API returns `images: [{ url, thumb_url }, ...]`. Legacy payloads
    // (older cached responses) may still return bare strings — handle both.
    _imgUrls(img) {
      if (typeof img === 'string') return { full: img, thumb: img };
      if (img && typeof img === 'object') {
        const full = img.url || img.thumb_url || '';
        const thumb = img.thumb_url || img.url || '';
        return { full, thumb };
      }
      return { full: '', thumb: '' };
    }

    _render() {
      if (this._skeleton) this._skeleton.style.display = 'none';
      if (!this._body) return;

      const all = this._allReviews;
      if (all.length === 0) {
        // Empty-state still renders an actionable "Write the first review"
        // button (or its "Submit another" sibling when the local ledger
        // marks the product as already-reviewed). Both carry
        // data-action="open-write" and need _bindEvents() to wire the click
        // handler. Skipping it here was a regression that left the empty-
        // state CTA visually correct but inert.
        this._body.innerHTML = this._emptyHTML();
        this._bindEvents();
        return;
      }

      const sorted = this._getSorted();
      const visible = sorted.slice(0, this._visibleCount);
      const hasMoreInMemory = visible.length < sorted.length;
      const showLoadMore = hasMoreInMemory || !!this._nextCursor;
      /* "Showing X of Y" is honest only when we know Y. If a Klaviyo
         cursor is still pending, Y is an underestimate, so we fall
         back to "Showing X reviews" and let the count update once the
         next page lands. */
      const knownTotal = !this._nextCursor;
      const allPhotos = all.flatMap((r) =>
        (r.images || []).map((img) => ({ ...this._imgUrls(img), author: r.author }))
      );

      this._body.innerHTML = `
        ${allPhotos.length > 0 ? this._photosHTML(allPhotos) : ''}
        ${this._controlsHTML(sorted.length)}
        <div class="dd-reviews__list">
          ${visible.map((r) => this._cardHTML(r)).join('')}
        </div>
        ${showLoadMore ? this._loadMoreHTML(visible.length, knownTotal ? sorted.length : null) : ''}
      `;

      this._bindEvents();
    }

    _photosHTML(photos) {
      const imgs = photos
        .slice(0, 20)
        .map(
          (p) =>
            `<img class="dd-reviews__photo" src="${this._esc(p.thumb)}" data-full="${this._esc(p.full)}" alt="Review photo by ${this._esc(p.author)}" loading="lazy" decoding="async">`
        )
        .join('');
      return `
        <div class="dd-reviews__photos">
          <div class="dd-reviews__photos-label">Review Photos</div>
          <div class="dd-reviews__photos-scroll" role="region" aria-label="Review photos" tabindex="0">${imgs}</div>
        </div>`;
    }

    _controlsHTML(total) {
      const writeBtn = this._writeCtaHTML('Write a Review', 'Login to leave review');
      return `
        <div class="dd-reviews__controls">
          <span class="dd-reviews__showing">${total} review${total !== 1 ? 's' : ''}</span>
          <div class="dd-reviews__filters">
            <select class="dd-reviews__select" data-action="sort" aria-label="Sort reviews">
              <option value="-created"${this._sortBy === '-created' ? ' selected' : ''}>Most Recent</option>
              <option value="-rating"${this._sortBy === '-rating' ? ' selected' : ''}>Highest Rated</option>
              <option value="rating"${this._sortBy === 'rating' ? ' selected' : ''}>Lowest Rated</option>
              <option value="images"${this._sortBy === 'images' ? ' selected' : ''}>With Photos</option>
            </select>
            ${writeBtn}
          </div>
        </div>`;
    }

    _canWriteReview() {
      // Write form requires the Klaviyo public key. If the merchant hasn't
      // configured it yet, we hide the CTA rather than showing a broken form.
      return !!this.klaviyoCompanyId;
    }

    _cardHTML(review) {
      const date = this._relativeDate(review.created);

      let imagesHTML = '';
      if (review.images?.length > 0) {
        imagesHTML = `<div class="dd-reviews__card-images">
          ${review.images
            .map((img) => {
              const { full, thumb } = this._imgUrls(img);
              return `<img class="dd-reviews__card-img" src="${this._esc(thumb)}" data-full="${this._esc(full)}" alt="Review photo by ${this._esc(review.author)}" loading="lazy" decoding="async">`;
            })
            .join('')}
        </div>`;
      }

      let replyHTML = '';
      if (review.public_reply) {
        replyHTML = `<div class="dd-reviews__card-reply">
          <div class="dd-reviews__reply-author">${this._esc(review.public_reply.author || 'Drop Dead Extensions')}</div>
          <div class="dd-reviews__reply-body">${this._esc(review.public_reply.content)}</div>
        </div>`;
      }

      // Drop Dead's brand is pro-only — anyone who can reach a product page,
      // purchase, and submit a review has already cleared the stylist approval
      // gate. So every card gets a verified chip. Klaviyo's public reviews
      // API doesn't accept ad-hoc metadata on a review record, so we can't
      // persist the submitter's professional_type alongside the review and
      // every chip normalizes to the baseline "Verified Stylist" label —
      // the credential every approved Drop Dead account holds. If we later
      // enrich reviews via a sidecar channel (Klaviyo profile/event), the
      // `review.customer_type` / `review.metadata.customer_type` reads below
      // will pick it up automatically without UI changes. The helper lives
      // in `dd-klaviyo-review-core.js` so the PDP and the sitewide review
      // nudge always agree on the icon + label.
      const rawType =
        (review.metadata && review.metadata.customer_type) ||
        review.customer_type ||
        '';
      // Inline fallback badge-check glyph for defense-in-depth: if the core
      // helper hasn't loaded (network flake, blocker, stale cache), the
      // badge still renders with an icon instead of a text-only chip. This
      // mirrors `DDKlaviyoReview.VERIFIED_BADGE_ICON` in dd-klaviyo-review-core.js.
      const fallbackIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>';
      const badge =
        (window.DDKlaviyoReview && window.DDKlaviyoReview.verifiedBadge)
          ? window.DDKlaviyoReview.verifiedBadge(rawType)
          : { key: 'stylist', label: 'Verified Stylist', iconSVG: fallbackIcon };
      const badgeHTML = `<span class="dd-reviews__card-verified dd-reviews__card-verified--${badge.key}" title="${this._esc(badge.label)}">
        ${badge.iconSVG}
        <span class="dd-reviews__card-verified-label">${this._esc(badge.label)}</span>
      </span>`;

      return `
        <article class="dd-reviews__card" aria-label="Review by ${this._esc(review.author)}, ${review.rating} stars">
          <header class="dd-reviews__card-head">
            <div class="dd-reviews__card-meta">
              <div class="dd-reviews__card-name-row">
                <span class="dd-reviews__card-name">${this._esc(review.author)}</span>
                ${badgeHTML}
              </div>
              <div class="dd-reviews__card-sub">
                <span class="dd-reviews__card-stars">${this._starsHTML(review.rating, 14)}</span>
                <span class="dd-reviews__card-date">${date}</span>
              </div>
            </div>
          </header>
          ${review.title ? `<div class="dd-reviews__card-title">${this._esc(review.title)}</div>` : ''}
          <div class="dd-reviews__card-body">${this._esc(review.content)}</div>
          ${imagesHTML}
          ${replyHTML}
        </article>`;
    }

    _loadMoreHTML(shown, total) {
      /* `total` is `null` when Klaviyo still has more pages (the true
         total is unknown until the next fetch lands) — in that case
         we omit "of Y" so the line isn't a contradiction next to the
         Load More button. */
      const count = (total != null) ? `Showing ${shown} of ${total} reviews` : `Showing ${shown} reviews`;
      return `
        <div class="dd-reviews__load-more-wrap">
          <button class="dd-reviews__load-more" data-action="load-more">Load More Reviews</button>
          <div class="dd-reviews__pagination-count">${count}</div>
        </div>`;
    }

    _emptyHTML() {
      const stars = Array.from({ length: 5 }, () =>
        `<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z"/></svg>`
      ).join('');
      const writeBtn = this._writeCtaHTML(
        'Write the first review',
        'Login to leave review',
        'dd-reviews__write-btn--empty'
      );
      return `
        <div class="dd-reviews__empty">
          <div class="dd-reviews__empty-stars">${stars}</div>
          <div class="dd-reviews__empty-heading">No reviews yet</div>
          <div class="dd-reviews__empty-body">Be the first to share your experience.</div>
          ${writeBtn}
        </div>`;
    }

    /**
     * Build the write-a-review CTA. Logged-in pros go straight to the write
     * modal (`data-action="open-write"`). Guests instead open the sitewide
     * registration/login iframe overlay via `data-registration-overlay-open`
     * + `data-overlay-path="/login"` — the same attributes every other guest-
     * gated CTA on the site uses (PDP buy buttons, cart drawer sign-in, etc.).
     * `dd-registration-overlay.js` installs a single document-level delegated
     * click listener that intercepts those attributes and opens the iframe at
     * `apply.dropdeadextensions.com/login`, so no wiring is needed here and
     * the iframe is typically prefetched on idle before the click. Returns
     * empty string if Klaviyo isn't configured yet, since neither path can
     * succeed.
     */
    _writeCtaHTML(loggedInLabel, guestLabel, extraClass) {
      if (!this._canWriteReview()) return '';
      const cls = `dd-reviews__write-btn${extraClass ? ' ' + extraClass : ''}`;
      if (this.customerLoggedIn) {
        // Gate repeat submissions on this PDP per the one-review-per-
        // customer-per-order-item policy. The PDP submit path doesn't
        // carry an order context, so the ledger check is product-scoped
        // (any stored entry for this product trips the gate). A "Submit
        // another" escape hatch stays available for the honest case where
        // the first POST didn't actually land in Klaviyo's moderation
        // queue — the gate is a hint, not a hard lock.
        const core = window.DDKlaviyoReview;
        const alreadyReviewed =
          core &&
          typeof core.wasReviewSubmitted === 'function' &&
          core.wasReviewSubmitted({ productId: this.productId });
        if (alreadyReviewed) {
          return `
            <div class="dd-reviews__already-reviewed">
              <span class="dd-reviews__already-reviewed-chip" role="status">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>
                Review submitted — awaiting moderation
              </span>
              <button type="button" class="dd-reviews__already-reviewed-link" data-action="open-write">Submit another</button>
            </div>`;
        }
        return `<button type="button" class="${cls}" data-action="open-write">${this._esc(loggedInLabel)}</button>`;
      }
      return `<button type="button" class="${cls}" data-registration-overlay-open data-overlay-path="/login" aria-haspopup="dialog">${this._esc(guestLabel)}</button>`;
    }

    _renderError() {
      if (this._skeleton) this._skeleton.style.display = 'none';
      if (this._body) {
        this._body.innerHTML = '<div class="dd-reviews__error">Reviews temporarily unavailable.</div>';
      }
    }

    _starsHTML(rating, size) {
      return Array.from({ length: 5 }, (_, i) => {
        const n = i + 1;
        let fill = Math.min(100, Math.max(0, (rating - n + 1) * 100));
        const filled = fill >= 100 ? '#1c1917' : (fill <= 0 ? '#d6d3d1' : null);
        if (filled) {
          return `<svg width="${size}" height="${size}" viewBox="0 0 20 20"><path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z" fill="${filled}"/></svg>`;
        }
        const id = `rs${size}${n}${Math.random().toString(36).slice(2, 6)}`;
        return `<svg width="${size}" height="${size}" viewBox="0 0 20 20">
          <defs><linearGradient id="${id}"><stop offset="${fill}%" stop-color="#1c1917"/><stop offset="${fill}%" stop-color="#d6d3d1"/></linearGradient></defs>
          <path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z" fill="url(#${id})"/></svg>`;
      }).join('');
    }

    _relativeDate(iso) {
      if (!iso) return '';
      const diff = Date.now() - new Date(iso).getTime();
      const days = Math.floor(diff / 86400000);
      if (days < 1) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days} days ago`;
      if (days < 30) { const w = Math.floor(days / 7); return `${w} week${w > 1 ? 's' : ''} ago`; }
      if (days < 365) { const m = Math.floor(days / 30); return `${m} month${m > 1 ? 's' : ''} ago`; }
      const y = Math.floor(days / 365);
      return `${y} year${y > 1 ? 's' : ''} ago`;
    }

    _esc(str) {
      if (!str) return '';
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }

    _bindEvents() {
      this.querySelectorAll('[data-action="sort"]').forEach((el) => {
        el.addEventListener('change', (e) => {
          this._sortBy = e.target.value;
          this._render();
        });
      });

      this.querySelectorAll('[data-action="load-more"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sorted = this._getSorted();
          const hasMoreInMemory = this._visibleCount < sorted.length;
          this._visibleCount += this._pageSize;
          if (hasMoreInMemory) {
            /* Pure client-side reveal — no network. Re-render so the
               next chunk paints; button keeps showing if we still
               haven't exhausted in-memory reviews OR a cursor exists. */
            this._render();
            return;
          }
          if (this._nextCursor) {
            /* In-memory list exhausted but Klaviyo has more pages.
               `_fetchReviews` is guarded by `_loading`, appends to
               `_allReviews`, and calls `_render` itself — the next
               render will surface the bumped `_visibleCount`. */
            btn.disabled = true;
            this._fetchReviews(this._nextCursor);
          }
        });
      });

      this.querySelectorAll('.dd-reviews__photo, .dd-reviews__card-img').forEach((img) => {
        img.addEventListener('click', () => {
          // Prefer full-resolution source for the lightbox; fall back to src.
          const full = img.getAttribute('data-full') || img.src;
          this._openLightbox(full);
        });
      });

      this.querySelectorAll('[data-action="open-write"]').forEach((btn) => {
        btn.addEventListener('click', () => this._openWriteModal());
      });
    }

    /* ── Write-a-review modal ───────────────────────────────── */

    _openWriteModal(prefill) {
      if (!this._canWriteReview()) return;
      if (this._writeModal) return; // already open

      /* If the caller didn't pass an explicit prefill (i.e. user
         clicked the "Write a review" button manually, or the
         `?write_review=1` deep-link fired), check sessionStorage for
         a draft from a previous session on this same product. This
         is the eviction-recovery path: if the page was reloaded
         while the photo picker was open, the next time the user
         clicks "Write a review" they get their draft back —
         transparent recovery, no surprise modals popping open on
         plain PDP visits.

         Hydration runs AFTER `_bindWriteModalEvents` so the rating
         picker's visual state and the photo list render through the
         same code path as a normal user click — eliminates a class
         of "draft restored but visual state desynced" bugs. */
      let draft = prefill && typeof prefill === 'object' ? prefill : null;
      if (!draft && this.customerLoggedIn) {
        draft = this._readDraft();
      }
      const isRestore = !!draft;

      /* Bump the encoding session counter so any in-flight ingest
         loop from a previous open of this modal bails on its next
         await boundary instead of pushing stale photos into the new
         instance's `_pendingPhotos`. Always reset the pending array
         AFTER the bump (in case a defensive in-flight push slips in
         between). */
      this._encodeSession = (this._encodeSession || 0) + 1;
      this._encodeBusy = false;
      this._pendingPhotos = [];

      const modal = document.createElement('div');
      modal.className = 'dd-reviews__modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'dd-reviews-modal-title');
      // Guests get a sign-in prompt instead of the form. Submission is gated
      // to logged-in customers so we can trust the verified email from the
      // Shopify session (prevents review spoofing) — Drop Dead's storefront
      // is pro-only anyway, so the account is what earns the "Verified
      // Stylist" badge.
      modal.innerHTML = this.customerLoggedIn
        ? this._writeFormHTML()
        : this._signInPromptHTML();

      document.body.appendChild(modal);
      this._writeModal = modal;
      // Prevent the page from scrolling behind the modal on mobile.
      this._prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this._bindWriteModalEvents();

      if (draft && this.customerLoggedIn) this._hydrateDraft(draft);

      // Move focus into the dialog for keyboard + screen-reader users.
      // When restoring a draft, prefer the textarea (most likely place
      // they were typing) over the rating picker so resume-and-keep-
      // typing is one tap on mobile.
      const focusSelector = !this.customerLoggedIn
        ? '[data-dd-rv-signin]'
        : draft && draft.content
          ? '#dd-rv-content'
          : '[data-dd-rv-rating] button';
      const first = modal.querySelector(focusSelector);
      if (first) {
        first.focus();
        // Park the caret at the end of any restored text so the user
        // can keep typing without first having to tap to deselect.
        if (first.tagName === 'TEXTAREA' && typeof first.setSelectionRange === 'function') {
          const end = first.value.length;
          try { first.setSelectionRange(end, end); } catch (_) { /* noop */ }
        }
      }
    }

    _hydrateDraft(draft) {
      const modal = this._writeModal;
      if (!modal) return;

      const ratingValue = modal.querySelector('[data-dd-rv-rating-value]');
      const ratingPicker = modal.querySelector('[data-dd-rv-rating]');
      const ratingNum = parseInt(draft.rating, 10);
      if (ratingValue && ratingPicker && ratingNum >= 1 && ratingNum <= 5) {
        ratingValue.value = String(ratingNum);
        ratingPicker.querySelectorAll('button').forEach((btn) => {
          const v = parseInt(btn.dataset.rating, 10);
          btn.classList.toggle('is-filled', v <= ratingNum);
        });
      }

      const setVal = (sel, value) => {
        const el = modal.querySelector(sel);
        if (el && typeof value === 'string') el.value = value;
      };
      setVal('#dd-rv-title', draft.title || '');
      setVal('#dd-rv-content', draft.content || '');
      // Author defaults to the verified customer name; only override
      // if the draft genuinely has a different value (someone using a
      // salon handle).
      if (draft.author && draft.author !== this.customerName) {
        setVal('#dd-rv-author', draft.author);
      }

      // Photos: drafts only ever contain already-encoded data URIs
      // (see `_saveDraftPhotos`), so we hydrate straight into
      // `_pendingPhotos` and render. Names are deliberately generic
      // ("Restored photo N") since the original `File.name` was lost
      // when the draft was serialized — file names aren't displayed
      // in the UI anyway, only used for alt text.
      if (Array.isArray(draft.photos) && draft.photos.length) {
        const MAX_PHOTOS = 4;
        draft.photos.slice(0, MAX_PHOTOS).forEach((dataUri, i) => {
          if (typeof dataUri === 'string' && dataUri.startsWith('data:image/')) {
            this._pendingPhotos.push({
              dataUri,
              name: `Restored photo ${i + 1}`,
            });
          }
        });
        const photoList = modal.querySelector('[data-dd-rv-photo-list]');
        if (photoList) this._renderPhotoList(photoList);
      }

      // Refresh the text-key timestamp so the TTL clock restarts
      // from the moment of restore. Without this, a user who picks
      // up a draft 28 minutes after the original keystroke would
      // see it expire 2 minutes later — confusing if they're
      // actively editing.
      this._saveDraftText();

      // Repaint character counters against the restored field
      // values. _wireCharCounters already did an initial paint
      // before hydration ran (so counters showed 0 / max
      // momentarily), so this is the corrective pass.
      modal.querySelectorAll('[data-dd-rv-counter-for]').forEach((counter) => {
        const inputId = counter.getAttribute('data-dd-rv-counter-for');
        const input = modal.querySelector(`#${CSS.escape(inputId)}`);
        if (input) this._renderCharCounter(counter, input);
      });

      // Surface a soft hint that this is a restored session — quiet,
      // not an error, so it sits in the same `[data-dd-rv-error]`
      // surface but unstyled as an error. We use the live error
      // element only when there's no existing error to avoid
      // clobbering a real one. Auto-dismisses after 4s.
      const errorEl = modal.querySelector('[data-dd-rv-error]');
      if (errorEl && errorEl.hidden) {
        errorEl.hidden = false;
        errorEl.classList.add('dd-reviews__form-error--info');
        errorEl.textContent = 'Restored your in-progress review.';
        setTimeout(() => {
          // Only clear if we still own the message (user hasn't hit
          // submit and triggered a real error in the meantime).
          if (errorEl.textContent === 'Restored your in-progress review.') {
            errorEl.hidden = true;
            errorEl.classList.remove('dd-reviews__form-error--info');
            errorEl.textContent = '';
          }
        }, 4000);
      }
    }

    _signInPromptHTML() {
      return `
        <div class="dd-reviews__modal-backdrop" data-dd-rv-backdrop></div>
        <div class="dd-reviews__modal-panel dd-reviews__modal-panel--compact">
          <button type="button" class="dd-reviews__modal-close" data-dd-rv-close aria-label="Close">&times;</button>
          <div class="dd-reviews__signin">
            <svg class="dd-reviews__signin-icon" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="2"/>
              <path d="M24 14v10l6 4" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="dd-reviews__modal-title" id="dd-reviews-modal-title">Sign in to write a review</h3>
            <p class="dd-reviews__modal-subtitle">
              Reviews on Drop Dead are written by verified stylists. Sign in to your pro account to leave a review for <strong>${this._esc(this.productName)}</strong>.
            </p>
            <div class="dd-reviews__signin-actions">
              <a href="${this._esc(this.loginUrl)}" class="dd-reviews__form-submit" data-dd-rv-signin>Sign in</a>
              <button type="button" class="dd-reviews__form-cancel" data-dd-rv-close>Not now</button>
            </div>
          </div>
        </div>
      `;
    }

    _closeWriteModal() {
      if (!this._writeModal) return;
      const modal = this._writeModal;
      // Detach references immediately so a second call (rapid double
      // tap on the close button, parallel ESC key) can't kick off a
      // second exit animation against a node already mid-tear-down.
      this._writeModal = null;
      document.body.style.overflow = this._prevBodyOverflow || '';

      /* Bump the encoding session counter BEFORE clearing
         `_pendingPhotos` so any in-flight ingest loop sees the
         mismatch on its next await checkpoint and bails without
         pushing into a stale array. Without this, a user who picks
         a photo and immediately hits Cancel would have the encoded
         photo arrive ~500ms later and leak into `_pendingPhotos`,
         then resurface as a "ghost photo" the next time the modal
         opens. */
      this._encodeSession = (this._encodeSession || 0) + 1;
      this._encodeBusy = false;
      this._encodeQueue = null;

      /* Explicit close (Cancel button, ESC, backdrop click, X) is
         treated as "discard draft." Tier-2 draft persistence exists
         to survive *involuntary* tab evictions, not to second-guess
         a user who deliberately dismissed the modal. The success
         path also clears the draft (see `_submitReview`) so the
         only state where a draft survives is "modal closed by the
         platform without user intent" — exactly what we want. */
      this._clearDraft();
      // Drop in-memory photo references too — keeps the next open
      // from inheriting stale data URIs if the user clicks "Write a
      // review" again immediately.
      this._pendingPhotos = [];

      // Honour reduced-motion: skip the exit animation entirely.
      const reducedMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) {
        modal.remove();
        return;
      }

      // Play the reverse animation defined in dd-reviews.css under
      // `.dd-reviews__modal.is-closing` (fade-out backdrop + pop-out
      // panel, ~160ms with a sharp ease-in). Listen for animationend
      // on the panel — if for any reason the event never fires
      // (animation rule stripped, browser bug, GPU stall) a 220ms
      // safety timeout still removes the node.
      modal.classList.add('is-closing');
      const panel = modal.querySelector('.dd-reviews__modal-panel');
      let removed = false;
      const finish = () => {
        if (removed) return;
        removed = true;
        modal.remove();
      };
      if (panel) {
        panel.addEventListener('animationend', finish, { once: true });
      }
      setTimeout(finish, 220);
    }

    _writeFormHTML() {
      const starBtns = Array.from({ length: 5 }, (_, i) => {
        const n = i + 1;
        return `<button type="button" data-rating="${n}" aria-label="${n} star${n > 1 ? 's' : ''}">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.12l2.47 5.01 5.53.8-4 3.9.94 5.49L10 13.77l-4.94 2.6.94-5.49-4-3.9 5.53-.8L10 1.12z" fill="currentColor"/></svg>
        </button>`;
      }).join('');

      // Form is only rendered when the customer is logged in, so we can
      // surface the verified identity as a read-only chip instead of asking
      // for email again. Name stays editable as a display name since some
      // pros use a salon handle.
      const identityChip = `<p class="dd-reviews__form-note">Submitting as <strong>${this._esc(this.customerEmail)}</strong>. Your review will appear after moderation.</p>`;

      return `
        <div class="dd-reviews__modal-backdrop" data-dd-rv-backdrop></div>
        <div class="dd-reviews__modal-panel">
          <button type="button" class="dd-reviews__modal-close" data-dd-rv-close aria-label="Close">&times;</button>
          <h3 class="dd-reviews__modal-title" id="dd-reviews-modal-title">Write a Review</h3>
          <p class="dd-reviews__modal-subtitle">${this._esc(this.productName)}</p>

          <form class="dd-reviews__form" data-dd-rv-form novalidate>
            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label">Your rating <span aria-hidden="true">*</span></label>
              <div class="dd-reviews__rating-picker" data-dd-rv-rating role="radiogroup" aria-label="Star rating">
                ${starBtns}
              </div>
              <input type="hidden" name="rating" value="" data-dd-rv-rating-value required>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-title">Review title</label>
              <input type="text" id="dd-rv-title" name="title" maxlength="120" class="dd-reviews__form-input" placeholder="Sums up your experience in a few words" aria-describedby="dd-rv-title-counter">
              <div class="dd-reviews__char-counter" id="dd-rv-title-counter" data-dd-rv-counter-for="dd-rv-title" data-max="120" aria-live="polite">0 / 120</div>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-content">Your review <span aria-hidden="true">*</span></label>
              <textarea id="dd-rv-content" name="content" class="dd-reviews__form-textarea" rows="5" maxlength="255" placeholder="Quick take — what worked, what didn't (255 char limit)." required aria-describedby="dd-rv-content-counter"></textarea>
              <div class="dd-reviews__char-counter" id="dd-rv-content-counter" data-dd-rv-counter-for="dd-rv-content" data-max="255" data-min="10" aria-live="polite">0 / 255</div>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-author">Display name <span aria-hidden="true">*</span></label>
              <input type="text" id="dd-rv-author" name="author" maxlength="120" class="dd-reviews__form-input" value="${this._esc(this.customerName)}" required aria-describedby="dd-rv-author-counter">
              <div class="dd-reviews__char-counter" id="dd-rv-author-counter" data-dd-rv-counter-for="dd-rv-author" data-max="120" aria-live="polite">0 / 120</div>
            </div>

            <div class="dd-reviews__form-field">
              <label class="dd-reviews__form-label" for="dd-rv-photos">Add photos <span class="dd-reviews__form-optional">(optional, up to 4)</span></label>
              <input
                type="file"
                id="dd-rv-photos"
                class="dd-reviews__photo-input"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                data-dd-rv-photo-input
              >
              <label for="dd-rv-photos" class="dd-reviews__photo-drop" data-dd-rv-photo-drop>
                <svg class="dd-reviews__photo-drop-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                <span class="dd-reviews__photo-drop-title">Add photos</span>
                <span class="dd-reviews__photo-drop-hint">JPEG, PNG, WebP or HEIC · 15&nbsp;MB max each</span>
              </label>
              <ul class="dd-reviews__photo-list" data-dd-rv-photo-list></ul>
            </div>

            ${identityChip}

            <div class="dd-reviews__form-error" data-dd-rv-error role="alert" hidden></div>

            <div class="dd-reviews__form-actions">
              <button type="button" class="dd-reviews__form-cancel" data-dd-rv-close>Cancel</button>
              <button type="submit" class="dd-reviews__form-submit" data-dd-rv-submit>Submit review</button>
            </div>
          </form>

          <div class="dd-reviews__form-success" data-dd-rv-success hidden>
            <svg class="dd-reviews__success-icon" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="currentColor" stroke-width="2"/>
              <path d="M14 24l7 7 13-14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h4 class="dd-reviews__success-title">Thanks for your review!</h4>
            <p class="dd-reviews__success-body">It's been submitted to our team and will appear on the page after approval.</p>
            <button type="button" class="dd-reviews__form-submit" data-dd-rv-close>Close</button>
          </div>
        </div>
      `;
    }

    _bindWriteModalEvents() {
      const modal = this._writeModal;
      if (!modal) return;

      const close = () => this._closeWriteModal();
      modal.querySelectorAll('[data-dd-rv-close], [data-dd-rv-backdrop]').forEach((el) => {
        el.addEventListener('click', close);
      });
      this._escHandler = (e) => {
        if (e.key === 'Escape') close();
      };
      document.addEventListener('keydown', this._escHandler);

      // Star picker — hover + click sync to hidden input.
      const picker = modal.querySelector('[data-dd-rv-rating]');
      const ratingValue = modal.querySelector('[data-dd-rv-rating-value]');
      if (picker && ratingValue) {
        const setVisual = (n) => {
          picker.querySelectorAll('button').forEach((btn) => {
            const v = parseInt(btn.dataset.rating, 10);
            btn.classList.toggle('is-filled', v <= n);
          });
        };
        picker.addEventListener('mouseover', (e) => {
          const btn = e.target.closest('button[data-rating]');
          if (btn) setVisual(parseInt(btn.dataset.rating, 10));
        });
        picker.addEventListener('mouseleave', () => {
          setVisual(parseInt(ratingValue.value, 10) || 0);
        });
        picker.addEventListener('click', (e) => {
          const btn = e.target.closest('button[data-rating]');
          if (!btn) return;
          ratingValue.value = btn.dataset.rating;
          setVisual(parseInt(btn.dataset.rating, 10));
        });
      }

      /* Photo uploader — accumulate already-encoded JPEG data URIs
         on the instance so the user can add photos in multiple
         batches and remove individual ones before submitting. We
         resize + re-encode on pick (NOT at submit time) for two
         reasons: (1) iOS Safari evicts background tabs holding many
         MB of raw HEIC `File` objects when the system photo picker
         is open; converting to a ~150-300KB JPEG data URI as soon
         as a photo is selected drops in-memory footprint ~10-30×.
         (2) Data URIs are serializable, so the draft persistence
         layer (`_saveDraftPhotos`) can checkpoint photos to
         sessionStorage and survive a reload — a raw `File` cannot.
         Each entry is `{ dataUri, name }` so the thumbnail keeps an
         accessible alt label even after the underlying File is
         GC'd. */
      this._pendingPhotos = this._pendingPhotos || [];
      const photoInput = modal.querySelector('[data-dd-rv-photo-input]');
      const photoList = modal.querySelector('[data-dd-rv-photo-list]');
      const photoDrop = modal.querySelector('[data-dd-rv-photo-drop]');
      const MAX_PHOTOS = 4;

      /* Capture the encoding session id for THIS modal mount.
         `_openWriteModal` bumps `this._encodeSession` on every open
         and on close, so any await resolving against a stale
         session means the modal was closed (or replaced by a
         second open) while we were encoding. Bailing on stale
         resolves prevents two known bugs:
           1. Ghost photos in next session: encoded photo arrives
              after _pendingPhotos was reset on close, leaks into
              the next open.
           2. Cross-modal contamination: rapid close+reopen
              produces a new `_writeModal` whose photoList element
              is a different DOM node than the one our closure
              captured. */
      const sessionId = this._encodeSession;
      const submitBtn = modal.querySelector('[data-dd-rv-submit]');

      const setEncoding = (busy) => {
        if (sessionId !== this._encodeSession) return;
        this._encodeBusy = !!busy;
        if (photoDrop) photoDrop.classList.toggle('is-encoding', !!busy);
        if (photoDrop) {
          if (busy) photoDrop.setAttribute('aria-busy', 'true');
          else photoDrop.removeAttribute('aria-busy');
        }
        // Block submit while encoding so a Submit click before the
        // last photo finishes doesn't POST a partial photo set. The
        // button label change makes the gate visible — without it,
        // a user could click Submit and watch the modal sit there
        // for a few seconds with no feedback.
        if (submitBtn) {
          submitBtn.disabled = !!busy;
          if (busy) {
            submitBtn.dataset.prevText = submitBtn.textContent;
            submitBtn.textContent = 'Processing photos…';
          } else if (submitBtn.dataset.prevText) {
            submitBtn.textContent = submitBtn.dataset.prevText;
            delete submitBtn.dataset.prevText;
          }
        }
      };

      /* Single-flight ingest queue. Each call awaits the previous
         one's completion before computing slot count, so two rapid
         "Add photos" taps can't both grab `slots = 4` when only one
         slot is actually available. The slot recount happens INSIDE
         the lock, so it's always accurate at the moment of decision. */
      const ingestFiles = (files) => {
        const incoming = Array.from(files || []);
        if (!incoming.length) return Promise.resolve();
        const prior = this._encodeQueue || Promise.resolve();
        const next = prior.then(() => this._ingestFilesInternal(incoming, modal, photoList, photoDrop, submitBtn, sessionId, setEncoding));
        // Swallow rejection on the chain so a single failure
        // doesn't poison subsequent ingests.
        this._encodeQueue = next.catch(() => {});
        return next;
      };

      if (photoInput && photoList) {
        photoInput.addEventListener('change', () => {
          const incoming = Array.from(photoInput.files || []);
          // Always reset the input value first so re-picking the same
          // file after a remove still fires `change`. Doing this
          // BEFORE awaiting the encode also drops the browser's
          // internal FileList reference asap, freeing the raw bytes.
          photoInput.value = '';
          if (!incoming.length) return;
          ingestFiles(incoming);
        });

        photoList.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-dd-rv-photo-remove]');
          if (!btn) return;
          const idx = parseInt(btn.dataset.ddRvPhotoRemove, 10);
          if (Number.isNaN(idx)) return;
          this._pendingPhotos.splice(idx, 1);
          this._renderPhotoList(photoList);
          // Touch text key too so its `ts` reflects the most recent
          // edit — keeps TTL fresh while the user is actively
          // working with the modal.
          this._saveDraftText();
          this._saveDraftPhotos();
        });
      }

      // Basic drag-and-drop onto the drop label. The native file input
      // handles click + keyboard; this just lets users drop images from
      // Finder/Explorer without opening a picker.
      if (photoDrop && photoInput) {
        ['dragenter', 'dragover'].forEach((evt) => {
          photoDrop.addEventListener(evt, (e) => {
            e.preventDefault();
            photoDrop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((evt) => {
          photoDrop.addEventListener(evt, (e) => {
            e.preventDefault();
            photoDrop.classList.remove('is-dragover');
          });
        });
        photoDrop.addEventListener('drop', (e) => {
          const files = e.dataTransfer && e.dataTransfer.files;
          if (!files || !files.length) return;
          // Filter to images here (the file picker already does via
          // `accept`, but `dataTransfer` is unfiltered) before
          // delegating to the same encode-on-pick pipeline so dropped
          // files match the picker path's memory + persistence story.
          const images = Array.from(files).filter(
            (file) => file && file.type && file.type.startsWith('image/')
          );
          if (!images.length) return;
          ingestFiles(images);
        });
      }

      /* Text-field draft autosave — `input` fires on every keystroke
         and the rating picker fires synchronously on click. We
         don't debounce: a debounce would create a race where the
         page evicts after the last keystroke but before the timer
         fires, losing the most recent edit (the exact failure mode
         we're trying to prevent). Saves only the text key, which is
         cheap (~1KB JSON.stringify + setItem) — the photo blob is
         persisted separately by `_saveDraftPhotos` on add/remove. */
      const ratingPicker = modal.querySelector('[data-dd-rv-rating]');
      if (ratingPicker) {
        ratingPicker.addEventListener('click', (e) => {
          if (e.target.closest('button[data-rating]')) this._saveDraftText();
        });
      }
      modal.querySelectorAll('#dd-rv-title, #dd-rv-content, #dd-rv-author').forEach((el) => {
        el.addEventListener('input', () => this._saveDraftText());
      });

      // Live character counters — wire after autosave so a single
      // `input` event repaints both the counter and the draft.
      this._wireCharCounters(modal);

      const form = modal.querySelector('[data-dd-rv-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this._submitReview(form);
        });
      }
    }

    /* Live character counters for title / body / display name.
       Each counter is a `<div data-dd-rv-counter-for="<input-id>"
       data-max="<n>">` next to its input. Format:

           "1,234 / 10,000"

       The counter element gets one of three state modifiers:
         (no class)      — under 80% of max
         --warn          — 80% ≤ chars < 100% of max
         --danger        — 100% of max (hit the cap)

       The body field also carries `data-min="10"` so we can render
       a "X more to go" hint below the minimum threshold — keeps
       users from hitting the "10+ characters" submit-time error. */
    _wireCharCounters(modal) {
      const counters = modal.querySelectorAll('[data-dd-rv-counter-for]');
      counters.forEach((counter) => {
        const inputId = counter.getAttribute('data-dd-rv-counter-for');
        const input = modal.querySelector(`#${CSS.escape(inputId)}`);
        if (!input) return;
        const update = () => this._renderCharCounter(counter, input);
        input.addEventListener('input', update);
        // Paint once at bind time so a hydrated draft shows the
        // right state without waiting for the user to type.
        update();
      });
    }

    _renderCharCounter(counter, input) {
      const max = parseInt(counter.getAttribute('data-max') || '', 10) || 0;
      const min = parseInt(counter.getAttribute('data-min') || '', 10) || 0;
      const len = (input.value || '').length;
      // Locale-formatted numbers so 10,000 reads cleanly (vs.
      // 10000 which looks like a stale CSS variable).
      const fmt = (n) => n.toLocaleString();

      let label;
      if (min > 0 && len < min) {
        // Under-min UX: tell the user how many more they need.
        // Reads as encouragement rather than a hard error, since
        // the submit handler is what enforces the floor.
        const remaining = min - len;
        label = `${fmt(len)} / ${fmt(max)} · ${remaining} more to go`;
      } else {
        label = `${fmt(len)} / ${fmt(max)}`;
      }
      counter.textContent = label;

      counter.classList.remove(
        'dd-reviews__char-counter--warn',
        'dd-reviews__char-counter--danger',
      );
      if (max > 0) {
        const ratio = len / max;
        if (ratio >= 1) {
          counter.classList.add('dd-reviews__char-counter--danger');
        } else if (ratio >= 0.8) {
          counter.classList.add('dd-reviews__char-counter--warn');
        }
      }
    }

    /* Pulled out of the closure in `_bindWriteModalEvents` so it
       can read/write the right instance state across the encode
       queue. Each await checkpoint re-checks (a) the modal is
       still open, (b) the encode session id hasn't been bumped by
       close+reopen — bailing in either case keeps stale photos out
       of the next open. */
    async _ingestFilesInternal(incoming, modal, photoList, photoDrop, submitBtn, sessionId, setEncoding) {
      const MAX_PHOTOS = 4;

      // Recount slots NOW (inside the lock) — earlier calls may
      // have completed encoding and pushed since the user picked.
      const slots = MAX_PHOTOS - this._pendingPhotos.length;
      if (slots <= 0) return;
      const accepted = incoming.slice(0, slots);
      if (!accepted.length) return;

      const core = window.DDKlaviyoReview;
      const errorEl = modal.querySelector('[data-dd-rv-error]');
      const showError = (msg) => {
        if (!errorEl) return;
        // Drop the info-banner styling if it's lingering from a
        // restore so a real error reads as red.
        errorEl.classList.remove('dd-reviews__form-error--info');
        errorEl.hidden = false;
        errorEl.textContent = msg;
      };

      if (!core || typeof core.filesToImages !== 'function') {
        showError('Photo upload is still loading. Please try again in a moment.');
        return;
      }

      setEncoding(true);
      try {
        for (const file of accepted) {
          // Bail if the modal was closed (or replaced) mid-encode.
          // The push that follows would otherwise leak into the
          // next session's `_pendingPhotos`.
          if (sessionId !== this._encodeSession) return;
          if (!this._writeModal) return;

          try {
            const [dataUri] = await core.filesToImages([file], { maxCount: 1 });
            // Re-check after the await — encoding can take 100s of
            // ms on iPhones, easily long enough for a user to hit
            // Cancel.
            if (sessionId !== this._encodeSession) return;
            if (!this._writeModal) return;

            if (dataUri) {
              this._pendingPhotos.push({
                dataUri,
                name: (file && file.name) || `Photo ${this._pendingPhotos.length + 1}`,
              });
            }
          } catch (err) {
            // Per-photo error — keep going on the next file rather
            // than failing the whole batch.
            if (sessionId === this._encodeSession && this._writeModal) {
              showError(
                `${(file && file.name) || 'A photo'}: ${(err && err.message) || 'could not be processed.'}`
              );
            }
          }

          // Render + persist after each successful photo so the user
          // sees progress when adding many at once. The encode loop
          // blocks the main thread per photo, so this is the only
          // window we get to paint a thumbnail update.
          if (sessionId === this._encodeSession && this._writeModal) {
            this._renderPhotoList(photoList);
            this._saveDraftPhotos();
          }
        }
      } finally {
        setEncoding(false);
      }
    }

    _renderPhotoList(listEl) {
      if (!listEl) return;
      /* Each pending photo is now an already-encoded JPEG data URI,
         so the thumbnail is just `<img src="<dataUri>">` — no
         createObjectURL/revokeObjectURL ceremony, no blob: handle
         leaks. The data URI doubles as the thumbnail source AND the
         submit-time payload, so what the user sees is exactly what
         gets POSTed to Klaviyo. */
      listEl.innerHTML = this._pendingPhotos
        .map((photo, i) => {
          const name = this._esc((photo && photo.name) || `Photo ${i + 1}`);
          const src = (photo && photo.dataUri) || '';
          return `<li class="dd-reviews__photo-item">
            <img src="${src}" alt="${name}">
            <button type="button" class="dd-reviews__photo-remove" data-dd-rv-photo-remove="${i}" aria-label="Remove ${name}">&times;</button>
          </li>`;
        })
        .join('');
    }

    disconnectedCallback() {
      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
    }

    async _submitReview(form) {
      const errorEl = form.querySelector('[data-dd-rv-error]');
      const submitBtn = form.querySelector('[data-dd-rv-submit]');
      const hideError = () => {
        errorEl.hidden = true;
        errorEl.textContent = '';
        // Drop the info variant if a restore banner was lingering.
        errorEl.classList.remove('dd-reviews__form-error--info');
      };
      const showError = (msg) => {
        errorEl.classList.remove('dd-reviews__form-error--info');
        errorEl.hidden = false;
        errorEl.textContent = msg;
      };
      hideError();

      /* If a photo encode is still in flight, refuse to submit
         rather than POST a partial photo set. The submit button
         should already be disabled by `setEncoding(true)` in the
         ingest pipeline, but this is defence-in-depth — a tampered
         DOM or a Submit triggered via Enter-key on a text field
         could bypass the disabled state. The encode pipeline calls
         `setEncoding(false)` in a `finally` block so this gate
         clears even on per-photo failures. */
      if (this._encodeBusy) {
        showError('Photos are still processing — please wait a moment.');
        return;
      }

      // Defence-in-depth: the form should never render for guests (see
      // `_openWriteModal`), but if someone tampered with the DOM or the
      // session expired mid-session, refuse to submit rather than POST an
      // unverified email to Klaviyo.
      if (!this.customerLoggedIn || !this.customerEmail) {
        showError('Please sign in to submit a review.');
        return;
      }

      const fd = new FormData(form);
      const rating = parseInt(fd.get('rating') || '', 10);
      const author = (fd.get('author') || '').toString().trim();
      const title = (fd.get('title') || '').toString().trim();
      const content = (fd.get('content') || '').toString().trim();
      // Email always comes from the verified Shopify session — never from user
      // input — so reviewers can't impersonate another customer's address.
      const email = this.customerEmail;

      if (!rating || rating < 1 || rating > 5) { showError('Please select a star rating.'); return; }
      if (!author) { showError('Please enter a display name.'); return; }
      if (!content || content.length < 10) { showError('Please write at least a sentence (10+ characters).'); return; }

      /* Submit-busy visual state — text-only shimmer. We wrap the
         label in a `<span class="dd-reviews__submit-label">` so CSS
         can apply a moving gradient via `background-clip: text` to
         the text alone, leaving the button's solid background +
         border intact. The shimmer signals "we're working on it"
         without claiming determinate progress (the network call is
         a single Klaviyo POST, no progress events to drive a real
         bar). The class flip + innerHTML write are paired so the
         finally-style cleanup at error/success points can restore
         the original label cleanly via `_clearSubmitBusy`. */
      this._setSubmitBusy(submitBtn, true);

      // Delegate the POST shape + 202/4xx/429 handling to the shared core so
      // this section and the sitewide review nudge speak one Klaviyo dialect.
      // Loaded via <script src="dd-klaviyo-review-core.js"> in layout/theme.liquid
      // before this file; if it's missing we fall back to an inline submission
      // error rather than silently doing the wrong thing.
      const core = window.DDKlaviyoReview;
      if (!core || typeof core.postKlaviyoReview !== 'function') {
        showError('Review service is still loading. Please try again in a moment.');
        this._setSubmitBusy(submitBtn, false);
        return;
      }

      /* Photos are already encoded to JPEG data URIs at pick time
         (see `ingestFiles` in `_bindWriteModalEvents`) — Klaviyo's
         reviews endpoint accepts the encoded bytes directly, so we
         can pass them through. Encoding-on-pick instead of encoding-
         on-submit drops in-memory footprint ~10-30× while the modal
         is open (no raw HEIC `File` objects held), which is the
         primary defence against the iOS "tab evicted while photo
         picker is open → reload" failure mode. */
      const images = Array.isArray(this._pendingPhotos)
        ? this._pendingPhotos.map((p) => p && p.dataUri).filter(Boolean)
        : undefined;

      // Klaviyo's public `client/reviews` endpoint rejects any attribute
      // outside its documented schema, so we don't attach ad-hoc metadata
      // (source / customer_type) to the review itself. Badge flavor is
      // derived at display time from the fallback path — see
      // `verifiedBadge()` in dd-klaviyo-review-core.js. If we need submitter
      // professional_type persisted per review in the future it needs to
      // ride a separate Klaviyo event/profile channel.
      const result = await core.postKlaviyoReview({
        companyId: this.klaviyoCompanyId,
        email,
        author,
        title,
        content,
        rating,
        productId: this.productId,
        images,
      });

      if (result.ok) {
        // The review has been accepted by Klaviyo — drop the draft
        // immediately so a quick refresh or back/forward nav doesn't
        // re-open a now-redundant copy of the same review the user
        // just submitted. Doing it BEFORE markReviewSubmitted /
        // _showWriteSuccess keeps the order tight: any error in the
        // post-success rendering path can't leave a stale draft
        // alive. Also drop in-memory photos for the same reason.
        this._clearDraft();
        this._pendingPhotos = [];

        // Record the submission in the shared ledger so the "Write a review"
        // button on subsequent opens (or other tabs) renders as an
        // "awaiting approval" chip instead of re-prompting. The PDP submit
        // path doesn't carry an orderId, so the entry is stored under the
        // productId-only slot and any future order-scoped check matches
        // via the product prefix.
        if (typeof core.markReviewSubmitted === 'function') {
          core.markReviewSubmitted({
            productId: this.productId,
            rating,
            source: 'pdp',
          });
        }
        this._showWriteSuccess();
        // Re-render the surrounding reviews section so the "Write a
        // review" button flips to the "awaiting moderation" chip as soon
        // as the modal is dismissed. The modal is appended to <body>, not
        // to this element, so re-rendering here won't disturb the
        // currently-visible success panel.
        try { this._render(); } catch (_) { /* defensive; render errors shouldn't swallow submit success */ }
        // Tell any listeners (analytics, the sitewide review nudge, etc.) a
        // review was submitted. Bubbles to document so cross-surface listeners
        // (e.g. `<dd-review-nudge>` on the same tab) can dedupe by productId.
        this.dispatchEvent(new CustomEvent('dd-review:submitted', {
          bubbles: true,
          detail: {
            productId: this.productId,
            orderId: this.dataset.orderId || null,
            rating,
            source: 'pdp',
          },
        }));
        return;
      }

      if (result.rateLimited) {
        showError('Too many attempts right now. Please try again in a moment.');
      } else if (result.error === 'network' || !result.res) {
        showError('Could not reach the review service. Please try again in a moment.');
      } else {
        /* Length-related Klaviyo rejections (e.g. "title cannot
           exceed 255 characters", "content too long") have an
           undocumented server-side limit that isn't surfaced in
           the OpenAPI spec. When the Klaviyo error mentions a
           length constraint, surface it with a clear field-
           specific suggestion AND highlight the offending
           counter via the danger state so the user can see
           which field is over.

           We match on common substrings rather than the exact
           string because Klaviyo's error message shape isn't
           guaranteed stable across revisions. False positives
           are mostly harmless — the user sees the actual error
           text appended either way, plus a friendly preface. */
        const raw = String(result.error || '').toLowerCase();
        const isLengthError =
          /length|too\s*long|exceed|max(?:imum)?\s*\d+|character/.test(raw);
        if (isLengthError) {
          // Try to map the error to a specific field so we can
          // highlight its counter.
          let fieldId = null;
          if (/title/.test(raw)) fieldId = 'dd-rv-title';
          else if (/author|name/.test(raw)) fieldId = 'dd-rv-author';
          else if (/content|body|review/.test(raw)) fieldId = 'dd-rv-content';
          if (fieldId) {
            const counter = form.querySelector(`[data-dd-rv-counter-for="${fieldId}"]`);
            if (counter) {
              counter.classList.remove('dd-reviews__char-counter--warn');
              counter.classList.add('dd-reviews__char-counter--danger');
            }
            const field = form.querySelector(`#${fieldId}`);
            if (field) field.focus();
          }
          showError(
            `Your review is too long for our review service. Please shorten it and try again. (${result.error})`
          );
        } else {
          showError(result.error || 'Submission failed. Please try again.');
        }
      }
      this._setSubmitBusy(submitBtn, false);
    }

    /* Toggle the submit-busy state on the submit button. While
       busy, the label is wrapped in a `<span>` so CSS can apply a
       text-only shimmer (see `.dd-reviews__submit-label--shimmer`
       in dd-reviews.css) and `aria-busy` is set so assistive tech
       reads "busy" without us having to swap the visible label
       constantly. The original label is captured in `dataset.idleLabel`
       on first call so subsequent restores read the right text even
       if the button was instantiated with a localised string. */
    _setSubmitBusy(btn, busy) {
      if (!btn) return;
      const modal = this._writeModal;
      // Lock the photo input while submitting so a user picking a
      // photo mid-submit can't kick off the encode pipeline, which
      // would clobber the shimmer label by setting `textContent =
      // 'Processing photos…'`. Re-enabled on busy=false.
      const photoInput = modal && modal.querySelector('[data-dd-rv-photo-input]');
      if (busy) {
        if (!btn.dataset.idleLabel) {
          btn.dataset.idleLabel = btn.textContent;
        }
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('is-submitting');
        if (photoInput) photoInput.disabled = true;
        // Use innerHTML rather than textContent so the wrapper
        // span renders as an element, not as escaped markup.
        btn.innerHTML = '<span class="dd-reviews__submit-label dd-reviews__submit-label--shimmer">Submitting&hellip;</span>';
      } else {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-submitting');
        if (photoInput) photoInput.disabled = false;
        btn.textContent = btn.dataset.idleLabel || 'Submit review';
      }
    }

    _showWriteSuccess() {
      const modal = this._writeModal;
      if (!modal) return;
      const form = modal.querySelector('[data-dd-rv-form]');
      const success = modal.querySelector('[data-dd-rv-success]');
      if (form) form.hidden = true;
      if (!success) return;
      success.hidden = false;

      // The modal panel is `overflow-y: auto` with `max-height: 95vh` on
      // mobile, so if the submitter scrolled down to find the submit button
      // in a long form, the panel's scrollTop is now parked below the short
      // success card. Snap the panel back to the top and pull the success
      // element into view. Both paths are idempotent; running them together
      // covers nested scrollers we don't directly own.
      const panel = success.closest('.dd-reviews__modal-panel');
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReducedMotion ? 'auto' : 'smooth';
      if (panel && typeof panel.scrollTo === 'function') {
        panel.scrollTo({ top: 0, behavior });
      } else if (panel) {
        panel.scrollTop = 0;
      }
      try {
        success.scrollIntoView({ block: 'start', behavior });
      } catch (_) {
        success.scrollIntoView(true);
      }
    }

    _openLightbox(src) {
      const lb = document.createElement('div');
      lb.className = 'dd-reviews__lightbox';
      lb.innerHTML = `
        <button class="dd-reviews__lightbox-close" aria-label="Close">&times;</button>
        <img src="${this._esc(src)}" alt="Review photo">`;
      lb.addEventListener('click', e => {
        if (e.target === lb || e.target.closest('.dd-reviews__lightbox-close')) lb.remove();
      });
      document.addEventListener('keydown', function handler(e) {
        if (e.key === 'Escape') { lb.remove(); document.removeEventListener('keydown', handler); }
      });
      document.body.appendChild(lb);
    }
  }

  customElements.define('dd-reviews', DDReviews);
}
